'use client';

import { useRef, useState } from 'react';
import { submitLead } from '@/lib/kit-client';
import { leadAttributionFields, trackGaConversion } from '@/lib/attribution';
import { PARTNER_AUDIENCES, PARTNER_ROLES, PARTNER_TERMS, REFERRAL_SERVICES, commissionFor, pct } from '@/lib/partners';
import { money } from '@/lib/savings';
import { Arrow, Check } from './ui';

/* ------------------------------------------------------------ calculator */

/** Drag the number of introductions, pick what they buy, see the commission. */
export function PartnerCalculator() {
  const [count, setCount] = useState(3);
  const [serviceId, setServiceId] = useState(REFERRAL_SERVICES[0].id);
  const service = REFERRAL_SERVICES.find((s) => s.id === serviceId)!;
  const [planId, setPlanId] = useState(service.plans[0].id);
  const plan = service.plans.find((p) => p.id === planId) ?? service.plans[0];
  const each = commissionFor(plan.price);
  const total = each * count;
  const plus = plan.priceIsFrom ? '+' : '';

  const pick = (id: typeof serviceId) => {
    setServiceId(id);
    setPlanId(REFERRAL_SERVICES.find((s) => s.id === id)!.plans[0].id);
  };

  return (
    <div className="grid grid-cols-1 gap-6 rounded-[26px] border-2 border-[#09090b] bg-ink-900 p-5 sm:p-8 lg:grid-cols-[1fr_auto] lg:items-center lg:gap-12">
      <div>
        <label htmlFor="pc-count" className="text-sm font-semibold text-mist-400">
          Businesses you introduce who become customers
        </label>
        <div className="mt-3 flex items-center gap-4">
          <input
            id="pc-count"
            type="range"
            min={1}
            max={20}
            value={count}
            onChange={(e) => setCount(Number(e.target.value))}
            className="h-2 w-full cursor-pointer accent-[var(--color-signal)]"
            aria-valuetext={`${count} businesses`}
          />
          <span className="w-12 shrink-0 text-right font-display text-3xl font-bold tabular-nums text-mist-100">{count}</span>
        </div>

        <p className="mt-6 text-sm font-semibold text-mist-400">What they sign up for</p>
        <div role="group" aria-label="Service" className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
          {REFERRAL_SERVICES.map((s) => (
            <button
              key={s.id}
              type="button"
              aria-pressed={s.id === serviceId}
              onClick={() => pick(s.id)}
              className={`press flex min-h-[3rem] items-center justify-between gap-2 rounded-xl border px-3 text-left text-sm font-semibold transition-colors sm:flex-col sm:items-start sm:justify-center sm:py-2 ${s.id === serviceId ? 'border-[#09090b] bg-[#09090b] text-white' : 'border-line-2 text-mist-300 hover:border-mist-500'}`}
            >
              {s.label}
              <span className={`text-xs font-normal ${s.id === serviceId ? 'text-white/60' : 'text-mist-500'}`}>{s.from}</span>
            </button>
          ))}
        </div>

        <p className="mt-5 text-sm font-semibold text-mist-400">The plan</p>
        <div role="group" aria-label="Plan" className="mt-3 flex flex-wrap gap-2">
          {service.plans.map((p) => (
            <button
              key={p.id}
              type="button"
              aria-pressed={p.id === plan.id}
              onClick={() => setPlanId(p.id)}
              className={`press min-h-[2.75rem] rounded-full border px-4 text-sm font-semibold transition-colors ${p.id === plan.id ? 'border-signal bg-signal text-on-signal' : 'border-line-2 text-mist-300 hover:border-mist-500'}`}
            >
              {p.name} <span className="font-normal opacity-70">{p.priceLabel}/mo</span>
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-[20px] bg-signal p-6 text-on-signal lg:w-72" aria-live="polite">
        <p className="text-sm font-semibold">You would earn</p>
        <p key={`${count}-${plan.id}`} className="pop-in mt-1 font-display text-[3.2rem] font-bold leading-none tracking-[-0.05em]">
          {money(total)}
          {plus}
        </p>
        <p className="mt-3 text-sm leading-snug">
          {money(each)}
          {plus} per customer: {pct(PARTNER_TERMS.commission)} of each of their first {PARTNER_TERMS.paidInvoices} successful paid invoices.
        </p>
      </div>
      <p className="text-xs leading-relaxed text-mist-500 lg:col-span-2">
        An example, not a promise. It assumes each business stays on the plan and pays its first {PARTNER_TERMS.paidInvoices} invoices, which carry their {pct(PARTNER_TERMS.referredDiscount)} discount.
        {service.freeDays ? ` The ${PARTNER_TERMS.freeDays} free days come first and are not an invoice.` : ''}
      </p>
    </div>
  );
}

/* ------------------------------------------------------------ the form */

const field =
  'w-full rounded-xl border border-line-2 bg-veil-2 px-4 py-3 text-sm text-mist-100 placeholder:text-mist-500 transition-colors focus:border-spark-500/60 focus:bg-veil-3 focus:outline-none';
const labelCls = 'mb-2 block text-sm font-semibold text-mist-100';

function Chips({ name, options, value, onChange }: { name: string; options: readonly string[]; value: string; onChange: (v: string) => void }) {
  return (
    <div role="radiogroup" aria-label={name} className="flex flex-wrap gap-2">
      {options.map((o) => {
        const on = value === o;
        return (
          <button
            key={o}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChange(o)}
            className={`press min-h-[2.75rem] rounded-full border px-4 text-sm font-medium transition-colors ${on ? 'border-[#09090b] bg-[#09090b] text-white' : 'border-line-2 bg-ink-900 text-mist-300 hover:border-mist-500'}`}
          >
            {o}
          </button>
        );
      })}
    </div>
  );
}

type Phase = 'form' | 'sending' | 'done' | 'error';

export function PartnerForm() {
  const [form, setForm] = useState({ name: '', email: '', company: '', note: '' });
  const [role, setRole] = useState('');
  const [audience, setAudience] = useState('');
  const [phase, setPhase] = useState<Phase>('form');
  const [missing, setMissing] = useState(false);
  const inFlight = useRef(false);
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function send(e: React.FormEvent) {
    e.preventDefault();
    if (!role || !audience) {
      setMissing(true);
      return;
    }
    if (inFlight.current) return;
    inFlight.current = true;
    setPhase('sending');
    /**
     * The company or website goes inside `message`, never as a `website` key:
     * KIT treats a non-empty `website` field as its spam honeypot and silently
     * drops the lead.
     */
    const message = [
      'Referral partner application',
      `Describes themselves as: ${role}`,
      `Can introduce: ${audience}`,
      `Company or website: ${form.company.trim() || 'not given'}`,
      form.note.trim() ? `Note: ${form.note.trim()}` : '',
    ]
      .filter(Boolean)
      .join('\n');
    try {
      await submitLead({ name: form.name, email: form.email, message, ...leadAttributionFields() });
      trackGaConversion('generate_lead', { enquiry: 'partner_application', role, audience });
      setPhase('done');
    } catch {
      setPhase('error');
    } finally {
      inFlight.current = false;
    }
  }

  if (phase === 'done') {
    return (
      <div className="rounded-[26px] border border-line-2 bg-ink-900 p-6 sm:p-8" role="status">
        <span className="pop-in flex h-14 w-14 items-center justify-center rounded-full bg-signal text-on-signal">
          <Check className="h-6 w-6" />
        </span>
        <h3 className="mt-5 font-display text-2xl font-bold tracking-tight">Application received</h3>
        <p className="mt-3 max-w-lg leading-relaxed text-mist-300">
          Thanks, {form.name.split(' ')[0]}. We review every application by hand. If it is a fit, we email {form.email} with the partner agreement and your referral code.
        </p>
      </div>
    );
  }

  const sending = phase === 'sending';
  return (
    <form method="post" onSubmit={send} className="grid gap-6 rounded-[26px] border border-line-2 bg-ink-900 p-5 sm:p-8">
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="pf-name" className={labelCls}>Your name</label>
          <input id="pf-name" required value={form.name} onChange={set('name')} className={field} placeholder="Jane Doe" autoComplete="name" />
        </div>
        <div>
          <label htmlFor="pf-email" className={labelCls}>Work email</label>
          <input id="pf-email" type="email" inputMode="email" required value={form.email} onChange={set('email')} className={field} placeholder="jane@company.com" autoComplete="email" />
        </div>
      </div>
      <div>
        <label htmlFor="pf-company" className={labelCls}>
          Company or website <span className="font-normal text-mist-500">(optional)</span>
        </label>
        <input id="pf-company" value={form.company} onChange={set('company')} className={field} placeholder="yourcompany.com" autoComplete="organization" />
      </div>
      <div>
        <p className={labelCls}>What best describes you?</p>
        <Chips name="What best describes you?" options={PARTNER_ROLES} value={role} onChange={setRole} />
      </div>
      <div>
        <p className={labelCls}>Who can you introduce to KIT?</p>
        <Chips name="Who can you introduce to KIT?" options={PARTNER_AUDIENCES} value={audience} onChange={setAudience} />
      </div>
      <div>
        <label htmlFor="pf-note" className={labelCls}>
          Anything we should know? <span className="font-normal text-mist-500">(optional)</span>
        </label>
        <textarea id="pf-note" rows={3} value={form.note} onChange={set('note')} className={field} placeholder="For example: I build sites for 20 HVAC companies in Texas." />
      </div>

      {missing && (!role || !audience) && (
        <p className="text-sm text-danger" role="alert">
          Pick what describes you and who you can introduce.
        </p>
      )}
      {phase === 'error' && (
        <p className="text-sm text-danger" role="alert">
          That did not go through. Nothing was sent. Try again, or email{' '}
          <a href="mailto:sales@kitagency.co" className="underline">
            sales@kitagency.co
          </a>
          .
        </p>
      )}

      <button type="submit" disabled={sending} className="btn btn-signal w-full px-6 py-4 text-[1rem] disabled:opacity-60 sm:w-auto sm:justify-self-start">
        {sending ? 'Sending…' : 'Apply to become a partner'}
        {!sending && <Arrow className="h-4 w-4" />}
      </button>
      <p className="-mt-2 text-xs leading-relaxed text-mist-500">We review every application before anyone earns commission. No account is created by this form.</p>
    </form>
  );
}
